import React, { useState } from 'react';
import { Sparkles, Loader, Crown, MessageSquare, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { BACKEND_URL, getSessionId, authHeaders } from '../lib/utils';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const T = {
  it: { ask: 'Chiedi all\'assistente', thinking: 'Sto guardando i prezzi…', target: 'Quanto vorresti pagarlo? (facoltativo)',
    proOnly: 'Incluso nel Pro', proCta: 'Attiva Pro', negotiate: 'Scrivimi il messaggio per trattare',
    copy: 'Copia', copied: 'Copiato', offer: 'Offerta consigliata', fair: 'Prezzo giusto',
    risks: 'Attenzione', error: 'L\'assistente non risponde ora. Riprova tra poco.',
    limit: 'Hai finito le domande di oggi.' },
  de: { ask: 'Assistent fragen', thinking: 'Ich schaue die Preise an…', target: 'Was möchtest du zahlen? (optional)',
    proOnly: 'Im Pro enthalten', proCta: 'Pro aktivieren', negotiate: 'Verhandlungsnachricht schreiben',
    copy: 'Kopieren', copied: 'Kopiert', offer: 'Empfohlenes Angebot', fair: 'Fairer Preis',
    risks: 'Achtung', error: 'Der Assistent antwortet gerade nicht. Bitte später nochmals.',
    limit: 'Deine Fragen für heute sind aufgebraucht.' },
  fr: { ask: 'Demander à l\'assistant', thinking: 'Je regarde les prix…', target: 'Combien voulez-vous payer ? (facultatif)',
    proOnly: 'Inclus dans Pro', proCta: 'Activer Pro', negotiate: 'Écris-moi le message pour négocier',
    copy: 'Copier', copied: 'Copié', offer: 'Offre conseillée', fair: 'Prix juste',
    risks: 'Attention', error: 'L\'assistant ne répond pas pour le moment.',
    limit: 'Vos questions du jour sont épuisées.' },
  en: { ask: 'Ask the assistant', thinking: 'Checking the prices…', target: 'What would you pay? (optional)',
    proOnly: 'Included with Pro', proCta: 'Activate Pro', negotiate: 'Write my negotiation message',
    copy: 'Copy', copied: 'Copied', offer: 'Suggested offer', fair: 'Fair price',
    risks: 'Watch out', error: 'The assistant is not responding right now.',
    limit: 'You have used today\'s questions.' },
};

const VERDICT = {
  affare: 'text-green-600 bg-green-500/10 border-green-500/25',
  giusto: 'text-blue-600 bg-blue-500/10 border-blue-500/25',
  caro: 'text-red-600 bg-red-500/10 border-red-500/25',
  incerto: 'text-zinc-600 bg-zinc-500/10 border-zinc-500/25',
};

export function AssistantListingBox({ listing }) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'it';
  const t = T[lang] || T.it;

  const [busy, setBusy] = useState('');
  const [target, setTarget] = useState('');
  const [result, setResult] = useState(null);
  const [nego, setNego] = useState(null);
  const [locked, setLocked] = useState(false);
  const [copied, setCopied] = useState(false);

  const call = async (path, body, setter, key) => {
    setBusy(key);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/${path}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ session_id: getSessionId(), source: listing.source,
          source_listing_id: listing.source_listing_id, lang, ...body }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        const code = b?.detail?.code;
        if (code === 'PRO_REQUIRED') setLocked(true);
        else toast.error(code === 'DAILY_LIMIT_REACHED' ? t.limit : (b?.detail?.message || t.error));
      } else {
        setter(await res.json());
      }
    } catch { toast.error(t.error); }
    setBusy('');
  };

  if (locked) {
    return (
      <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-4" data-testid="quickview-assistant-gate">
        <p className="text-sm font-medium flex items-center gap-2 mb-2">
          <Crown size={14} className="text-amber-500" /> {t.proOnly}
        </p>
        <Button size="sm" onClick={() => window.location.assign('/pro')}
          className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="quickview-assistant-upgrade">
          {t.proCta}
        </Button>
      </div>
    );
  }

  const a = result?.assessment;

  return (
    <div className="rounded-xl border border-border p-4 space-y-3" data-testid="quickview-assistant">
      {!a && (
        <>
          <div className="flex gap-2">
            <Input type="number" inputMode="numeric" value={target} onChange={(e) => setTarget(e.target.value)}
              placeholder={t.target} className="h-9 rounded-lg text-sm" data-testid="quickview-assistant-target" />
          </div>
          <Button size="sm" disabled={!!busy}
            onClick={() => call('listing-verdict', target ? { target_price: Number(target) } : {}, setResult, 'verdict')}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white gap-2" data-testid="quickview-assistant-ask">
            {busy === 'verdict' ? <><Loader size={14} className="animate-spin" /> {t.thinking}</>
              : <><Sparkles size={14} /> {t.ask}</>}
          </Button>
        </>
      )}

      {a && (
        <div data-testid="quickview-assistant-result">
          <span className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-bold uppercase tracking-wide mb-2 ${VERDICT[a.verdict] || VERDICT.incerto}`}
            data-testid="quickview-assistant-verdict">
            {a.verdict}
          </span>
          <p className="text-sm font-semibold mb-2">{a.headline}</p>
          {a.fair_price_chf != null && (
            <p className="text-xs text-muted-foreground mb-2">{t.fair}: <strong>CHF {a.fair_price_chf}</strong>
              {a.suggested_offer_chf != null && <> · {t.offer}: <strong>CHF {a.suggested_offer_chf}</strong></>}
            </p>
          )}
          {a.reasons?.length > 0 && (
            <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4 mb-2">
              {a.reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
          {a.risks?.length > 0 && (
            <p className="text-xs text-amber-600 mb-2"><strong>{t.risks}:</strong> {a.risks.join(' · ')}</p>
          )}
          {result?.market?.sample > 0 && (
            <p className="text-xs text-muted-foreground mb-3">
              CHF {result.market.median} ⌀ · {result.market.sample} annunci · CHF {result.market.p25}–{result.market.p75}
            </p>
          )}

          {!nego && (
            <Button size="sm" variant="outline" disabled={!!busy} className="w-full gap-2"
              onClick={() => call('negotiate', a.suggested_offer_chf ? { offer_price: a.suggested_offer_chf } : {}, setNego, 'nego')}
              data-testid="quickview-assistant-negotiate">
              {busy === 'nego' ? <><Loader size={14} className="animate-spin" /> {t.thinking}</>
                : <><MessageSquare size={14} /> {t.negotiate}</>}
            </Button>
          )}
          {nego?.negotiation && (
            <div className="mt-3 rounded-lg bg-muted p-3" data-testid="quickview-assistant-negotiation">
              <p className="text-sm whitespace-pre-line mb-2">{nego.negotiation.message}</p>
              {nego.negotiation.tip && <p className="text-xs text-muted-foreground mb-2">{nego.negotiation.tip}</p>}
              <Button size="sm" variant="ghost" className="gap-1.5 h-7 text-xs"
                onClick={() => {
                  navigator.clipboard?.writeText(nego.negotiation.message);
                  setCopied(true); setTimeout(() => setCopied(false), 2000);
                }} data-testid="quickview-assistant-copy">
                {copied ? <><Check size={12} /> {t.copied}</> : <><Copy size={12} /> {t.copy}</>}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
